import React, { useEffect, useState, Component } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../hooks/useLanguage';
import { SEO } from '../components/SEO';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { quizQuestions, quizCategories, countries } from '../data/cyberQuizData';
import { events } from '../data/eventsData';
import { ArrowRight, CheckCircle, Shield, User, Mail, Phone, Globe, MessageCircle, Calendar } from 'lucide-react';
export function CyberQuizPage() {
  const {
    language,
    t
  } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const eventId = searchParams.get('event');
  // State
  const [started, setStarted] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [score, setScore] = useState(0);
  const [answers, setAnswers] = useState<number[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [showResult, setShowResult] = useState(false);
  // Form State
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    country: 'Indonesia'
  });
  // Get event details if eventId is present
  const selectedEvent = eventId ? events.find(e => e.id === eventId) : null;
  // Handlers
  const handleStart = () => {
    setStarted(true);
    window.scrollTo(0, 0);
  };
  const handleAnswer = (points: number) => {
    const newScore = score + points;
    setScore(newScore);
    setAnswers([...answers, points]);
    if (currentQuestion < quizQuestions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      setShowForm(true);
    }
  };
  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setShowForm(false);
    setShowResult(true);
    window.scrollTo(0, 0);
  };
  const getCategory = (totalScore: number) => {
    // Max score is 30 (10 questions * 3 points)
    // Normalize to 100 scale
    const normalizedScore = totalScore / (quizQuestions.length * 3) * 100;
    return quizCategories.find(c => normalizedScore >= c.minScore && normalizedScore <= c.maxScore) || quizCategories[0];
  };
  const getWhatsAppLink = () => {
    const category = getCategory(score);
    const categoryName = category.name[language] || category.name['en'];
    let message = `Halo genZAFE! 👋\n\nSaya baru saja menyelesaikan Cyber Quiz dan hasil saya adalah: *${categoryName}* (Score: ${Math.round(score / 30 * 100)}/100).`;
    if (selectedEvent) {
      const eventTitle = selectedEvent.title[language] || selectedEvent.title['en'];
      message += `\n\nSaya ingin mendaftar untuk acara: *${eventTitle}*`;
    } else {
      message += `\n\nSaya ingin mendaftar antrian peserta webinar berikutnya!`;
    }
    message += `\n\nData Saya:
Nama: ${formData.name}
Email: ${formData.email}
Negara: ${formData.country}
No HP: ${formData.phone}

Mohon infonya ya! Terima kasih.`;
    return `https://wa.me/628127634091?text=${encodeURIComponent(message)}`;
  };
  // Render Helpers
  const renderIntro = () => <div className="max-w-3xl mx-auto text-center py-12 px-4" data-id="element-503">
      {selectedEvent && <div className="mb-8 bg-blue-50 border border-blue-100 rounded-xl p-6 text-left max-w-2xl mx-auto" data-id="element-504">
          <div className="flex items-start gap-4" data-id="element-505">
            <div className="p-3 bg-blue-100 rounded-lg text-blue-600 hidden sm:block" data-id="element-506">
              <Calendar className="w-6 h-6" data-id="element-507" />
            </div>
            <div data-id="element-508">
              <span className="text-xs font-bold text-blue-600 uppercase tracking-wider mb-1 block" data-id="element-509">
                {language === 'id' ? 'Registrasi Acara' : 'Event Registration'}
              </span>
              <h3 className="text-lg font-bold text-slate-900 mb-1" data-id="element-510">
                {selectedEvent.title[language] || selectedEvent.title['en']}
              </h3>
              <p className="text-sm text-slate-600" data-id="element-511">
                {language === 'id' ? 'Untuk menyelesaikan pendaftaran, silakan ikuti kuis singkat ini untuk mengetahui profil risiko keamananmu.' : 'To complete registration, please take this quick quiz to determine your security risk profile.'}
              </p>
            </div>
          </div>
        </div>}

      <div className="mb-8 flex justify-center" data-id="element-512">
        <div className="p-4 bg-primary-50 rounded-full" data-id="element-513">
          <Shield className="w-16 h-16 text-primary-600" data-id="element-514" />
        </div>
      </div>
      <h1 className="text-4xl font-bold text-slate-900 mb-6" data-id="element-515">
        {language === 'id' ? 'Seberapa Jago Kamu Lawan Scammer?' : 'How Good Are You at Spotting Scams?'}
      </h1>
      <p className="text-xl text-slate-600 mb-8 leading-relaxed" data-id="element-516">
        {language === 'id' ? 'Ikuti kuis singkat ini untuk mengetahui level keamanan sibermu. Apakah kamu "Digital Noob" atau "Firewall Legend"? Cari tahu sekarang!' : 'Take this quick quiz to find out your cybersecurity level. Are you a "Digital Noob" or a "Firewall Legend"? Find out now!'}
      </p>
      <Button size="xl" onClick={handleStart} rightIcon={<ArrowRight className="w-5 h-5" data-id="element-518" />} data-id="element-517">
        {language === 'id' ? 'Mulai Kuis' : 'Start Quiz'}
      </Button>
    </div>;
  const renderQuestion = () => {
    const q = quizQuestions[currentQuestion];
    const progress = (currentQuestion + 1) / quizQuestions.length * 100;
    return <div className="max-w-2xl mx-auto px-4 py-8" data-id="element-519">
        <div className="mb-8" data-id="element-520">
          <div className="flex justify-between text-sm font-medium text-slate-500 mb-2" data-id="element-521">
            <span data-id="element-522">
              {language === 'id' ? 'Pertanyaan' : 'Question'}{' '}
              {currentQuestion + 1}/{quizQuestions.length}
            </span>
            <span data-id="element-523">{Math.round(progress)}%</span>
          </div>
          <div className="w-full bg-slate-200 rounded-full h-2.5" data-id="element-524">
            <div className="bg-primary-600 h-2.5 rounded-full transition-all duration-500" style={{
            width: `${progress}%`
          }} data-id="element-525"></div>
          </div>
        </div>

        <Card className="p-6 sm:p-8" data-id="element-526">
          <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-8" data-id="element-527">
            {q.question[language] || q.question['en']}
          </h2>
          <div className="space-y-4" data-id="element-528">
            {q.options.map((opt, idx) => <button key={idx} onClick={() => handleAnswer(opt.score)} className="w-full text-left p-4 rounded-xl border-2 border-slate-100 hover:border-primary-500 hover:bg-primary-50 transition-all duration-200 group" data-id="element-529">
                <div className="flex items-start" data-id="element-530">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full border-2 border-slate-300 group-hover:border-primary-500 mr-4 mt-0.5 flex items-center justify-center" data-id="element-531">
                    <div className="w-2.5 h-2.5 rounded-full bg-primary-500 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-532" />
                  </div>
                  <span className="text-lg text-slate-700 group-hover:text-slate-900" data-id="element-533">
                    {opt.text[language] || opt.text['en']}
                  </span>
                </div>
              </button>)}
          </div>
        </Card>
      </div>;
  };
  const renderForm = () => <div className="max-w-xl mx-auto px-4 py-8" data-id="element-534">
      <Card className="p-6 sm:p-8" data-id="element-535">
        <div className="text-center mb-8" data-id="element-536">
          <h2 className="text-2xl font-bold text-slate-900 mb-2" data-id="element-537">
            {language === 'id' ? 'Hampir Selesai!' : 'Almost Done!'}
          </h2>
          <p className="text-slate-600" data-id="element-538">
            {language === 'id' ? 'Isi data dirimu untuk melihat hasil dan menyelesaikan pendaftaran.' : 'Fill in your details to see your result and complete registration.'}
          </p>
        </div>

        <form onSubmit={handleFormSubmit} className="space-y-6" data-id="element-539">
          <div data-id="element-540">
            <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-541">
              {language === 'id' ? 'Nama Lengkap' : 'Full Name'}
            </label>
            <div className="relative" data-id="element-542">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5" data-id="element-543" />
              <input type="text" required className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500" placeholder="John Doe" value={formData.name} onChange={e => setFormData({
              ...formData,
              name: e.target.value
            })} data-id="element-544" />
            </div>
          </div>

          <div data-id="element-545">
            <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-546">
              Email
            </label>
            <div className="relative" data-id="element-547">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5" data-id="element-548" />
              <input type="email" required className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500" placeholder="john@example.com" value={formData.email} onChange={e => setFormData({
              ...formData,
              email: e.target.value
            })} data-id="element-549" />
            </div>
          </div>

          <div data-id="element-550">
            <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-551">
              {language === 'id' ? 'Nomor WhatsApp' : 'WhatsApp Number'}
            </label>
            <div className="relative" data-id="element-552">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5" data-id="element-553" />
              <input type="tel" required className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500" placeholder="+62..." value={formData.phone} onChange={e => setFormData({
              ...formData,
              phone: e.target.value
            })} data-id="element-554" />
            </div>
          </div>

          <div data-id="element-555">
            <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-556">
              {language === 'id' ? 'Negara' : 'Country'}
            </label>
            <div className="relative" data-id="element-557">
              <Globe className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5" data-id="element-558" />
              <select className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 appearance-none bg-white" value={formData.country} onChange={e => setFormData({
              ...formData,
              country: e.target.value
            })} data-id="element-559">
                {countries.map(c => <option key={c} value={c} data-id="element-560">
                    {c}
                  </option>)}
              </select>
            </div>
          </div>

          <Button type="submit" fullWidth size="lg" data-id="element-561">
            {language === 'id' ? 'Lihat Hasil & Daftar' : 'See Result & Register'}
          </Button>
        </form>
      </Card>
    </div>;
  const renderResult = () => {
    const category = getCategory(score);
    return <div className="max-w-2xl mx-auto px-4 py-8 text-center" data-id="element-562">
        <div className="mb-8 animate-fade-in" data-id="element-563">
          <span className="inline-block px-4 py-1 rounded-full bg-slate-100 text-slate-600 text-sm font-medium mb-4" data-id="element-564">
            {language === 'id' ? 'Hasil Analisa Kamu' : 'Your Analysis Result'}
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6" data-id="element-565">
            {formData.name},{' '}
            {language === 'id' ? 'kamu adalah seorang...' : 'you are a...'}
          </h2>

          <div className={`p-8 rounded-2xl border-2 mb-8 ${category.color} transform transition-all hover:scale-105 duration-300 shadow-lg`} data-id="element-566">
            <h3 className="text-4xl sm:text-5xl font-extrabold mb-4 tracking-tight" data-id="element-567">
              {category.name[language] || category.name['en']}
            </h3>
            <p className="text-lg sm:text-xl opacity-90 max-w-lg mx-auto leading-relaxed" data-id="element-568">
              {category.description[language] || category.description['en']}
            </p>
          </div>

          <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm mb-8" data-id="element-569">
            <h4 className="font-bold text-lg mb-4 text-slate-900" data-id="element-570">
              {selectedEvent ? language === 'id' ? 'Konfirmasi Pendaftaran' : 'Registration Confirmation' : language === 'id' ? 'Langkah Selanjutnya' : 'Next Steps'}
            </h4>

            {selectedEvent ? <div className="mb-6 bg-blue-50 p-4 rounded-lg border border-blue-100 text-left" data-id="element-571">
                <p className="text-sm text-slate-500 mb-1" data-id="element-572">
                  {language === 'id' ? 'Kamu mendaftar untuk:' : 'You are registering for:'}
                </p>
                <p className="font-bold text-blue-900 text-lg" data-id="element-573">
                  {selectedEvent.title[language] || selectedEvent.title['en']}
                </p>
                <div className="flex items-center gap-4 mt-2 text-sm text-slate-600" data-id="element-574">
                  <span className="flex items-center" data-id="element-575">
                    <Calendar className="w-4 h-4 mr-1" data-id="element-576" /> {selectedEvent.date}
                  </span>
                  <span className="flex items-center" data-id="element-577">
                    <User className="w-4 h-4 mr-1" data-id="element-578" /> {selectedEvent.speaker}
                  </span>
                </div>
              </div> : <ul className="text-left space-y-3 mb-6" data-id="element-579">
                <li className="flex items-start" data-id="element-580">
                  <CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-581" />
                  <span className="text-slate-600" data-id="element-582">
                    {language === 'id' ? 'Daftar webinar eksklusif kami' : 'Register for our exclusive webinar'}
                  </span>
                </li>
                <li className="flex items-start" data-id="element-583">
                  <CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-584" />
                  <span className="text-slate-600" data-id="element-585">
                    {language === 'id' ? 'Pelajari cara melindungi aset digitalmu' : 'Learn how to protect your digital assets'}
                  </span>
                </li>
                <li className="flex items-start" data-id="element-586">
                  <CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-587" />
                  <span className="text-slate-600" data-id="element-588">
                    {language === 'id' ? 'Bergabung dengan komunitas genZAFE' : 'Join the genZAFE community'}
                  </span>
                </li>
              </ul>}

            <a href={getWhatsAppLink()} target="_blank" rel="noopener noreferrer" className="block w-full" data-id="element-589">
              <button className="w-full bg-[#25D366] hover:bg-[#128C7E] text-white font-bold py-4 px-6 rounded-xl shadow-md hover:shadow-lg transition-all duration-200 flex items-center justify-center group" data-id="element-590">
                <MessageCircle className="w-6 h-6 mr-2 group-hover:animate-bounce" data-id="element-591" />
                {language === 'id' ? selectedEvent ? 'Kirim Pendaftaran via WhatsApp' : 'Daftar Webinar via WhatsApp' : selectedEvent ? 'Send Registration via WhatsApp' : 'Register Webinar via WhatsApp'}
              </button>
            </a>
            <p className="text-xs text-slate-400 mt-3" data-id="element-592">
              {language === 'id' ? 'Klik tombol di atas untuk mengirim pesan otomatis dengan hasil kuis kamu.' : 'Click the button above to send an automated message with your quiz result.'}
            </p>
          </div>

          <Button variant="outline" onClick={() => window.location.reload()} data-id="element-593">
            {language === 'id' ? 'Ulangi Kuis' : 'Retake Quiz'}
          </Button>
        </div>
      </div>;
  };
  return <div className="min-h-screen bg-slate-50" data-id="element-594">
      <SEO title={t('seo.cyberQuiz.title')} description={t('seo.cyberQuiz.desc')} data-id="element-595" />

      {!started && renderIntro()}
      {started && !showForm && !showResult && renderQuestion()}
      {showForm && !showResult && renderForm()}
      {showResult && renderResult()}
    </div>;
}